/**
 * Pozisyon Yönetim Paneli — Bordro Ayarları içinde admin için.
 *
 * Admin tek tıkla PositionTemplateManager modalını açarak:
 *  - Pozisyonları yeniden adlandırır (cascade: tüm personel otomatik güncellenir)
 *  - İki pozisyonu birleştirir (A → B, kaynak silinir)
 *  - Drift tespiti ile şablonda olmayan legacy pozisyonları toplu taşır
 * Rozet renkleri `getPozisyonColor` deterministik hash'e dayalı olduğu için
 * isim değişince yeni renk otomatik yeniden hesaplanır; parent `refresh()`
 * sonrası tüm sekmeler yeni rozetlerle render edilir.
 */
import React, { useEffect, useState } from "react";
import { Briefcase, AlertTriangle, Users, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { getPozisyonColor } from "@/lib/pozisyonColors";
import PositionTemplateManager from "@/components/personnel/PositionTemplateManager";

export default function PozisyonYonetimPanel({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [driftCount, setDriftCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        api.get("/position-templates"),
        api.get("/position-templates/drift"),
      ]);
      setTemplates(t.data?.rows || []);
      setDriftCount((d.data?.groups || []).length);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="pozisyon-yonetim-panel">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <Briefcase size={16} className="text-indigo-700 mt-0.5" />
        <div className="flex-1 min-w-[180px]">
          <h4 className="font-semibold text-sm text-slate-900">Pozisyon Yönetimi</h4>
          <p className="text-xs text-slate-600 mt-0.5">
            Pozisyonları yeniden adlandır, iki pozisyonu birleştir veya legacy drift
            değerlerini şablonlarla eşleştir. Değişiklikler tüm personele cascade uygulanır
            ve rozet renkleri otomatik yeniden hesaplanır.
          </p>
        </div>
        <button
          type="button"
          data-testid="open-pozisyon-manager-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
        >
          <ArrowRight size={13} /> Yönet
        </button>
      </div>

      {/* Compact chip preview — tüm pozisyonlar + rozet renkleri */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="pozisyon-preview">
        {loading && <span className="text-xs italic text-slate-400">Yükleniyor...</span>}
        {!loading && templates.length === 0 && (
          <span className="text-xs italic text-slate-400">Henüz pozisyon tanımlı değil</span>
        )}
        {!loading && templates.map((t) => {
          const c = getPozisyonColor(t.isim);
          return (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border"
              style={{ backgroundColor: c.chipBg, borderColor: c.chipBorder, color: c.chipText }}
              title={`${t.usage_count ?? 0} personel`}
              data-testid={`pozisyon-chip-${t.isim}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.dotBg }} />
              {t.isim}
              <span className="inline-flex items-center gap-0.5 opacity-70">
                <Users size={9} /> {t.usage_count ?? 0}
              </span>
            </span>
          );
        })}
        {driftCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full" title={`${driftCount} drift grubu — şablonda olmayan pozisyonlar`}>
            <AlertTriangle size={10} /> {driftCount} drift
          </span>
        )}
      </div>

      {open && (
        <PositionTemplateManager
          onClose={() => setOpen(false)}
          onChanged={() => { load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}
